import * as React from 'react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Select, { Option } from '@/components/elements/Form/Select';
import RaceProgramDailyNum from '@/components/elements/Text/RaceProgramDailyNum';
import { RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { getVelodromeByCode } from '@/lib/race';
import { toHalfWidth } from '@/utility/utils';

import RaceProgramDetail from './RaceProgramDetail';
import {
  Content,
  DailyNum,
  DailyNumList,
  ProgramName,
  RaceProgramDetailWrapper,
  SelectWrapper,
  Wrapper,
} from './style';

export enum PageType {
  RaceCard = 'racecard',
  Result = 'result',
}
interface RaceProgramInfoProps {
  raceProgram: RaceProgram;
  racePrograms?: RaceProgram[];
  pageType?: PageType;
}

const RaceProgramInfo: React.FC<RaceProgramInfoProps> = React.memo(
  ({ raceProgram, racePrograms, pageType }) => {
    const navigate = useNavigate();

    const isAddRaceProgram = (rpIdx: number) => {
      if (racePrograms) {
        if (racePrograms[rpIdx].isCanceled) {
          const currentRp =
            racePrograms.length > rpIdx ? racePrograms[rpIdx] : null;
          const nextRp =
            racePrograms.length > rpIdx + 1 ? racePrograms[rpIdx + 1] : null;
          if (currentRp && nextRp && currentRp.dailyNum === nextRp.dailyNum) {
            // 「開催中止フラグ」がONで、次のデータに同じ日次がある → 「順延」なので中止になった方は追加しない
            return false;
          } else {
            // 「開催中止フラグ」がONで、次のデータに同じ日次がない → 「中止」なので追加する
            return true;
          }
        } else {
          // 「開催中止フラグ」がOFFなら「true」
          return true;
        }
      }
      return false;
    };
    const rps = useMemo(() => {
      const result: RaceProgram[] = [];
      if (racePrograms) {
        for (var i = 0; i < racePrograms.length; i++) {
          if (isAddRaceProgram(i)) {
            result.push(racePrograms[i]);
          }
        }
      }
      return result;
    }, [racePrograms]);

    const getRaceDailyNum = (rp: RaceProgram) => {
      const displayName = rp.dailyNum === 1 ? '初日' : `${rp.dailyNum}日目`;
      return rp.isLastDay ? '最終日' : displayName;
    };

    const getDailyNumList = () => {
      const dailyNumList: Option[] = [];
      if (rps === undefined) {
        return dailyNumList;
      }
      rps.forEach(rp => {
        dailyNumList.push({
          value: rp.heldDate.format('YYYY-MM-DD'),
          displayName: getRaceDailyNum(rp),
        } as Option);
      });
      return dailyNumList;
    };

    const getLinkPath = (velodromeCode: number, heldDate: string) => {
      switch (pageType) {
        case PageType.RaceCard: {
          return `/${
            getVelodromeByCode(velodromeCode).romanName
          }/racecard/${heldDate}`;
        }
        case PageType.Result: {
          return `/${
            getVelodromeByCode(velodromeCode).romanName
          }/race/${heldDate}/result`;
        }
        default: {
          throw new Error('未定義');
        }
      }
    };

    const disabledDayList = racePrograms
      ?.filter(rp => rp.races.length === 0)
      .map(rp => rp.heldDate.format('YYYY-MM-DD'));

    return (
      <Wrapper>
        <Content>
          <RaceProgramDetailWrapper>
            <RaceProgramDetail raceProgram={raceProgram} hasLink={false} />
          </RaceProgramDetailWrapper>
          {raceProgram.programName && (
            <ProgramName>{toHalfWidth(raceProgram.programName)}</ProgramName>
          )}
        </Content>
        {racePrograms && (
          <>
            <DailyNumList>
              {rps.map((rp, i) => (
                <DailyNum
                  key={i}
                  style={{
                    fontWeight: rp.id === raceProgram.id ? 'bold' : 'normal',
                  }}
                >
                  {rp.races.length ? (
                    <Link
                      to={{
                        pathname: getLinkPath(
                          rp.velodromeCode,
                          rp.heldDate.format('YYYY-MM-DD'),
                        ),
                      }}
                      state={{ cancelScroll: true }}
                    >
                      <RaceProgramDailyNum
                        isLastDay={rp.isLastDay}
                        dailyNum={rp.dailyNum}
                      />
                    </Link>
                  ) : (
                    <div>
                      <RaceProgramDailyNum
                        isLastDay={rp.isLastDay}
                        dailyNum={rp.dailyNum}
                      />
                    </div>
                  )}
                </DailyNum>
              ))}
            </DailyNumList>
            <SelectWrapper>
              <Select
                value={raceProgram.heldDate.format('YYYY-MM-DD')}
                disabledList={disabledDayList}
                width="100%"
                isCenter
                options={getDailyNumList()}
                onChange={e =>
                  navigate(
                    getLinkPath(raceProgram.velodromeCode, e.target.value),
                    { state: { cancelScroll: true } },
                  )
                }
              />
            </SelectWrapper>
          </>
        )}
      </Wrapper>
    );
  },
);

export default RaceProgramInfo;
